import { RINK, type GoalTag, type PhysicsEvent } from '../types';

export interface Vec {
  x: number;
  y: number;
}

export interface Paddle {
  pos: Vec;
  prev: Vec;
  target: Vec;
}

export interface Puck {
  pos: Vec;
  vel: Vec;
}

export type Phase = 'play' | 'goal-host' | 'goal-guest' | 'serve';

export interface GameState {
  tick: number;
  puck: Puck;
  host: Paddle;
  guest: Paddle;
  score: { host: number; guest: number };
  phase: Phase;
  /** Ticks remaining in current phase (for serve/goal pauses). */
  phaseTicks: number;
  /** Ring buffer of recent puck positions for the stalemate detector. */
  stuckBuf: Vec[];
  stuckBufIdx: number;
  stuckBufFill: number;
  // Per-serve tracking for goal-tag detection. Host-side state only —
  // the guest receives goal events with the `tag` already computed, so
  // these fields are dormant on the guest. Reset on every new serve.
  lastHitter: 'host' | 'guest' | null;
  serveHostHit: boolean;
  serveGuestHit: boolean;
  lastHitX: number;
  lastHitY: number;
  wallBouncesSinceHit: number;
  peakSpeedSinceHit: number;
  /** Tick of the most recent goal-post deflection (collidePost reflect).
   *  Used by the 'snipe' goal-tag detector. -1 = never. */
  lastPostHitTick: number;
  /** Side of the most recent goal — used for 'hat-trick' detection. */
  lastGoalScorer: 'host' | 'guest' | null;
  /** Consecutive goals by lastGoalScorer (resets when scorer changes). */
  consecutiveGoals: number;
  /** Tick at which the current serve transitioned to 'play' — used by
   *  the 'speedrun' goal-tag detector. */
  lastServeStartTick: number;
  /** Per-match physics tuning, set once at newGame(). Mutated by
   *  modifier `apply()` calls; thereafter constant for the match. */
  config: PhysicsConfig;
  /** When true, step() is a no-op. Toggled by the player; synced to the
   *  guest in the next state broadcast. */
  paused: boolean;
}

/** Per-match physics tuning. Modifiers patch this at match start. */
export interface PhysicsConfig {
  /** Per-tick velocity multiplier on the puck (drag). 1.0 = frictionless. */
  friction: number;
  /** Cap on paddle slew rate, world units per tick. */
  paddleMaxSpeed: number;
  /** Cap on puck speed, world units per tick. */
  puckMaxSpeed: number;
  /** Paddle collision + visual radius (the "fat" axis — across paddle). */
  paddleRadius: number;
  /** Half-height of the pill paddle's straight section. When equal to
   *  paddleRadius, the paddle is a circle. When larger, it's a vertical
   *  capsule (Pong-style tall bat). */
  paddleHalfHeight: number;
  /** Puck collision + visual radius. */
  puckRadius: number;
  /** Host paddle x is clamped to [paddleRadius, hostXMax]. Pong mode
   *  narrows this so the paddle is pinned near its own goal. */
  hostXMax: number;
  /** Guest paddle x is clamped to [guestXMin, width - paddleRadius]. */
  guestXMin: number;
  /** Half-height of the goal mouth opening (gap in the rim wall). Pong
   *  sets this to RINK.height/2 so the entire back wall is the goal. */
  goalHalfHeight: number;
  /** Depth of the goal pocket chamber. Reaching the back wall scores. */
  pocketDepth: number;
  /** Number of pucks on the field. Double Puck = 2. */
  puckCount: number;
}

export function defaultPhysicsConfig(): PhysicsConfig {
  const pr = RINK.paddleRadius;
  return {
    friction: 0.998,
    paddleMaxSpeed: 22,
    puckMaxSpeed: RINK.maxPuckSpeed,
    paddleRadius: pr,
    paddleHalfHeight: pr,          // circle paddle by default
    puckRadius: RINK.puckRadius,
    hostXMax: RINK.width / 2 - pr,
    guestXMin: RINK.width / 2 + pr,
    goalHalfHeight: RINK.goalHalfHeight,
    pocketDepth: RINK.pocketDepth,
    puckCount: 1,
  };
}

const STUCK_BUF_LEN = 40;

const GOAL_PAUSE_TICKS = 60;  // 1s
const SERVE_PAUSE_TICKS = 45;

export function newGame(config: PhysicsConfig = defaultPhysicsConfig()): GameState {
  const s: GameState = {
    tick: 0,
    puck: { pos: { x: RINK.width / 2, y: RINK.height / 2 }, vel: { x: 0, y: 0 } },
    host: makePaddle(RINK.width * 0.2, RINK.height / 2),
    guest: makePaddle(RINK.width * 0.8, RINK.height / 2),
    score: { host: 0, guest: 0 },
    phase: 'serve',
    phaseTicks: SERVE_PAUSE_TICKS,
    stuckBuf: Array.from({ length: STUCK_BUF_LEN }, () => ({
      x: RINK.width / 2,
      y: RINK.height / 2,
    })),
    stuckBufIdx: 0,
    stuckBufFill: 0,
    lastHitter: null,
    serveHostHit: false,
    serveGuestHit: false,
    lastHitX: 0,
    lastHitY: 0,
    wallBouncesSinceHit: 0,
    peakSpeedSinceHit: 0,
    lastPostHitTick: -1,
    lastGoalScorer: null,
    consecutiveGoals: 0,
    lastServeStartTick: 0,
    config,
    paused: false,
  };
  return s;
}

function makePaddle(x: number, y: number): Paddle {
  return { pos: { x, y }, prev: { x, y }, target: { x, y } };
}

/** Reset a match in-place — keeps the same GameState object (so closures
 *  in the render/loop holding a reference stay valid) but zeros score,
 *  resets phase to serve, re-centers paddles + puck, clears all per-
 *  serve tracking. Config is untouched. */
export function resetMatch(s: GameState): void {
  s.tick = 0;
  s.puck.pos.x = RINK.width / 2;
  s.puck.pos.y = RINK.height / 2;
  s.puck.vel.x = 0;
  s.puck.vel.y = 0;
  const resetPaddle = (p: Paddle, x: number, y: number): void => {
    p.pos.x = x; p.pos.y = y;
    p.prev.x = x; p.prev.y = y;
    p.target.x = x; p.target.y = y;
  };
  resetPaddle(s.host, RINK.width * 0.2, RINK.height / 2);
  resetPaddle(s.guest, RINK.width * 0.8, RINK.height / 2);
  s.score.host = 0;
  s.score.guest = 0;
  s.phase = 'serve';
  s.phaseTicks = SERVE_PAUSE_TICKS;
  for (const p of s.stuckBuf) { p.x = s.puck.pos.x; p.y = s.puck.pos.y; }
  s.stuckBufIdx = 0;
  s.stuckBufFill = 0;
  s.lastHitter = null;
  s.serveHostHit = false;
  s.serveGuestHit = false;
  s.lastHitX = 0;
  s.lastHitY = 0;
  s.wallBouncesSinceHit = 0;
  s.peakSpeedSinceHit = 0;
  s.lastPostHitTick = -1;
  s.lastGoalScorer = null;
  s.consecutiveGoals = 0;
  s.lastServeStartTick = 0;
  s.paused = false;
}

export function setHostTarget(s: GameState, x: number, y: number): void {
  const pr = s.config.paddleRadius;
  const ph = s.config.paddleHalfHeight;
  s.host.target.x = clamp(x, pr, s.config.hostXMax);
  s.host.target.y = clamp(y, ph, RINK.height - ph);
}

export function setGuestTarget(s: GameState, x: number, y: number): void {
  const pr = s.config.paddleRadius;
  const ph = s.config.paddleHalfHeight;
  s.guest.target.x = clamp(x, s.config.guestXMin, RINK.width - pr);
  s.guest.target.y = clamp(y, ph, RINK.height - ph);
}

/**
 * Advance the simulation by one tick (1/60 s).
 *
 * If `events` is provided, the function appends physics events that
 * occurred this tick (collisions, wall bounces, goals). These are
 * consumed by the animation system and broadcast over the network so
 * both peers spawn the same effects.
 */
export function step(s: GameState, events?: PhysicsEvent[]): void {
  if (s.paused) return;
  s.tick++;

  movePaddle(s.host, s.config.paddleMaxSpeed);
  movePaddle(s.guest, s.config.paddleMaxSpeed);

  if (s.phase !== 'play') {
    s.stuckBufFill = 0;
    s.stuckBufIdx = 0;
    s.phaseTicks--;
    if (s.phaseTicks <= 0) {
      // Serve toward whoever was just scored on (or random on first serve).
      const dir =
        s.phase === 'goal-host' ? -1 // host scored, serve to host (left)
        : s.phase === 'goal-guest' ? 1
        : (Math.random() < 0.5 ? -1 : 1);
      s.puck.pos.x = RINK.width / 2;
      s.puck.pos.y = RINK.height / 2;
      const speed = 4;
      const angle = (Math.random() - 0.5) * 0.6;
      s.puck.vel.x = dir * speed * Math.cos(angle);
      s.puck.vel.y = speed * Math.sin(angle);
      // Reset per-serve tracking that drives goal-tag detection.
      s.lastHitter = null;
      s.serveHostHit = false;
      s.serveGuestHit = false;
      s.lastHitX = 0;
      s.lastHitY = 0;
      s.wallBouncesSinceHit = 0;
      s.peakSpeedSinceHit = 0;
      s.phase = 'play';
      s.lastServeStartTick = s.tick;
    }
    return;
  }

  // Puck motion.
  s.puck.pos.x += s.puck.vel.x;
  s.puck.pos.y += s.puck.vel.y;
  s.puck.vel.x *= s.config.friction;
  s.puck.vel.y *= s.config.friction;

  // Cap speed.
  const sp = Math.hypot(s.puck.vel.x, s.puck.vel.y);
  if (sp > s.config.puckMaxSpeed) {
    s.puck.vel.x = (s.puck.vel.x / sp) * s.config.puckMaxSpeed;
    s.puck.vel.y = (s.puck.vel.y / sp) * s.config.puckMaxSpeed;
  }
  // Track peak puck speed since the last paddle hit. Drives the
  // "banger" goal tag.
  const speedNow = sp > s.config.puckMaxSpeed ? s.config.puckMaxSpeed : sp;
  if (speedNow > s.peakSpeedSinceHit) s.peakSpeedSinceHit = speedNow;

  // Rounded corners first. Each corner is a quarter-circle inscribed in
  // the corner; the puck collides against the arc when its center is
  // farther from the arc center than `cornerRadius - puckRadius`.
  // Skips the axis-aligned wall checks in the corner zone so we don't
  // double-resolve.
  const cR = RINK.cornerRadius;
  const inCornerX = s.puck.pos.x < cR ? -1 : s.puck.pos.x > RINK.width - cR ? 1 : 0;
  const inCornerY = s.puck.pos.y < cR ? -1 : s.puck.pos.y > RINK.height - cR ? 1 : 0;

  if (inCornerX !== 0 && inCornerY !== 0) {
    // Arc center sits cornerRadius in from both adjacent walls.
    const acx = inCornerX < 0 ? cR : RINK.width - cR;
    const acy = inCornerY < 0 ? cR : RINK.height - cR;
    const dx = s.puck.pos.x - acx;
    const dy = s.puck.pos.y - acy;
    const d = Math.hypot(dx, dy);
    const maxD = cR - s.config.puckRadius;
    if (d > maxD) {
      let nx: number, ny: number;
      if (d < 0.001) {
        // Degenerate: puck at arc center. Push toward the corner so
        // the next tick has a sensible geometry.
        nx = inCornerX < 0 ? -1 : 1;
        ny = inCornerY < 0 ? -1 : 1;
        const n = Math.SQRT2;
        nx /= n; ny /= n;
      } else {
        nx = dx / d;
        ny = dy / d;
      }
      s.puck.pos.x = acx + nx * maxD;
      s.puck.pos.y = acy + ny * maxD;
      // Reflect velocity if moving outward (away from arc center =
      // toward the rink boundary).
      const dot = s.puck.vel.x * nx + s.puck.vel.y * ny;
      if (dot > 0) {
        s.puck.vel.x -= 2 * dot * nx;
        s.puck.vel.y -= 2 * dot * ny;
      }
      events?.push({
        e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: false, speed: sp,
      });
    }
  } else {
    // Top / bottom walls.
    if (s.puck.pos.y < s.config.puckRadius) {
      s.puck.pos.y = s.config.puckRadius;
      s.puck.vel.y = Math.abs(s.puck.vel.y);
      events?.push({ e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: true, speed: sp });
    } else if (s.puck.pos.y > RINK.height - s.config.puckRadius) {
      s.puck.pos.y = RINK.height - s.config.puckRadius;
      s.puck.vel.y = -Math.abs(s.puck.vel.y);
      events?.push({ e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: true, speed: sp });
    }

    // Left / right walls + goal pockets + posts.
    const goalTop = RINK.height / 2 - s.config.goalHalfHeight;
    const goalBot = RINK.height / 2 + s.config.goalHalfHeight;
    const pd = s.config.pocketDepth;

    // Goal posts: point-circle collisions at the corners where the rim
    // meets the goal mouth. A puck grazing one of these deflects — this
    // is the "bounce-out" mechanic for shots that don't go cleanly
    // between the posts.
    collidePost(s, 0, goalTop, sp, events);
    collidePost(s, 0, goalBot, sp, events);
    collidePost(s, RINK.width, goalTop, sp, events);
    collidePost(s, RINK.width, goalBot, sp, events);

    if (s.puck.pos.x < 0) {
      // Inside the LEFT pocket. Side walls are the inner edges of the
      // posts (y=goalTop and y=goalBot); back wall (x=-pd) is the goal
      // line.
      if (s.puck.pos.y < goalTop + s.config.puckRadius) {
        s.puck.pos.y = goalTop + s.config.puckRadius;
        s.puck.vel.y = Math.abs(s.puck.vel.y);
        events?.push({ e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: true, speed: sp });
      } else if (s.puck.pos.y > goalBot - s.config.puckRadius) {
        s.puck.pos.y = goalBot - s.config.puckRadius;
        s.puck.vel.y = -Math.abs(s.puck.vel.y);
        events?.push({ e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: true, speed: sp });
      }
      if (s.puck.pos.x < -pd + s.config.puckRadius) {
        s.score.guest++;
        s.phase = 'goal-guest';
        s.phaseTicks = GOAL_PAUSE_TICKS;
        events?.push({ e: 'goal', scorer: 'guest', x: s.puck.pos.x, y: s.puck.pos.y });
      }
    } else if (s.puck.pos.x < s.config.puckRadius) {
      // Approaching the left rim. The rim wall exists only OUTSIDE the
      // goal mouth — the mouth itself is the gateway into the pocket.
      if (s.puck.pos.y <= goalTop || s.puck.pos.y >= goalBot) {
        s.puck.pos.x = s.config.puckRadius;
        s.puck.vel.x = Math.abs(s.puck.vel.x);
        events?.push({ e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: false, speed: sp });
      }
    }

    if (s.puck.pos.x > RINK.width) {
      // Inside the RIGHT pocket.
      if (s.puck.pos.y < goalTop + s.config.puckRadius) {
        s.puck.pos.y = goalTop + s.config.puckRadius;
        s.puck.vel.y = Math.abs(s.puck.vel.y);
        events?.push({ e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: true, speed: sp });
      } else if (s.puck.pos.y > goalBot - s.config.puckRadius) {
        s.puck.pos.y = goalBot - s.config.puckRadius;
        s.puck.vel.y = -Math.abs(s.puck.vel.y);
        events?.push({ e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: true, speed: sp });
      }
      if (s.puck.pos.x > RINK.width + pd - s.config.puckRadius) {
        s.score.host++;
        s.phase = 'goal-host';
        s.phaseTicks = GOAL_PAUSE_TICKS;
        events?.push({ e: 'goal', scorer: 'host', x: s.puck.pos.x, y: s.puck.pos.y });
      }
    } else if (s.puck.pos.x > RINK.width - s.config.puckRadius) {
      if (s.puck.pos.y <= goalTop || s.puck.pos.y >= goalBot) {
        s.puck.pos.x = RINK.width - s.config.puckRadius;
        s.puck.vel.x = -Math.abs(s.puck.vel.x);
        events?.push({ e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: false, speed: sp });
      }
    }
  }

  // Paddle collisions.
  collide(s.puck, s.host, 'host', s.config, events);
  collide(s.puck, s.guest, 'guest', s.config, events);

  // ── Stalemate fallback ────────────────────────────────────────────────
  // Bounding-box detector: if every position in the last 60-tick window
  // fits inside a small box, the puck is trapped (bouncing in place
  // even if instantaneous speed is high). On detection we don't just
  // kick — we TELEPORT the puck to center with a random velocity.
  // That decisively breaks the bot's pin cycle: the bot is now ~half
  // a rink away, has to chase, and the puck has time to settle into
  // a non-pinned position.
  const buf = s.stuckBuf[s.stuckBufIdx]!;
  buf.x = s.puck.pos.x;
  buf.y = s.puck.pos.y;
  s.stuckBufIdx = (s.stuckBufIdx + 1) % STUCK_BUF_LEN;
  if (s.stuckBufFill < STUCK_BUF_LEN) s.stuckBufFill++;
  if (s.stuckBufFill >= STUCK_BUF_LEN) {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (let i = 0; i < STUCK_BUF_LEN; i++) {
      const p = s.stuckBuf[i]!;
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    // Path length over the window. Distinguishes "trapped puck bouncing
    // in a small area" (high path length, small span) from "puck idling
    // slowly in a small area" (low path length, small span). Only the
    // first is actually a stalemate worth resetting.
    let totalPath = 0;
    for (let i = 0; i < STUCK_BUF_LEN - 1; i++) {
      const aIdx = (s.stuckBufIdx + i) % STUCK_BUF_LEN;
      const bIdx = (s.stuckBufIdx + i + 1) % STUCK_BUF_LEN;
      const a = s.stuckBuf[aIdx]!;
      const b = s.stuckBuf[bIdx]!;
      totalPath += Math.hypot(b.x - a.x, b.y - a.y);
    }
    const span = Math.max(maxX - minX, maxY - minY);
    // Net displacement: oldest sample (stuckBufIdx now points to the
    // slot that will be overwritten next, so it holds the oldest entry)
    // → newest sample. A trapped puck oscillates back near its start
    // (low netDisp); a slow puck drifting through a small region has
    // netDisp ≈ totalPath, which exonerates it.
    const oldest = s.stuckBuf[s.stuckBufIdx]!;
    const newest = s.stuckBuf[(s.stuckBufIdx + STUCK_BUF_LEN - 1) % STUCK_BUF_LEN]!;
    const netDisp = Math.hypot(newest.x - oldest.x, newest.y - oldest.y);
    // Triggers when EITHER:
    //   - puck is essentially idle (tiny span, regardless of path), OR
    //   - puck is bouncing in a small area: small span, some motion,
    //     AND it's actually returning near where it started.
    const isIdle = span < 30;
    const isBouncing = span < 100 && totalPath > 80 && netDisp < 25;
    if (isIdle || isBouncing) {
      // Hard reset: teleport to center with random velocity.
      const fromX = s.puck.pos.x;
      const fromY = s.puck.pos.y;
      s.puck.pos.x = RINK.width / 2;
      s.puck.pos.y = RINK.height / 2;
      const angle = Math.random() * Math.PI * 2;
      s.puck.vel.x = Math.cos(angle) * 7;
      s.puck.vel.y = Math.sin(angle) * 7;
      // Reseed buffer to the new position so we don't immediately
      // re-trigger.
      for (let i = 0; i < STUCK_BUF_LEN; i++) {
        s.stuckBuf[i]!.x = s.puck.pos.x;
        s.stuckBuf[i]!.y = s.puck.pos.y;
      }
      s.stuckBufFill = 0;
      s.stuckBufIdx = 0;
      events?.push({
        e: 'reset',
        fromX, fromY,
        toX: s.puck.pos.x,
        toY: s.puck.pos.y,
      });
    }
  }

  // Process events in chronological order to update per-serve counters
  // and attach goal tags. Order is: walls → posts → goal → paddle hits,
  // so a goal mid-tick uses counter state built up by earlier events.
  if (events) {
    for (const ev of events) {
      if (ev.e === 'hit') {
        s.lastHitter = ev.paddle;
        if (ev.paddle === 'host') s.serveHostHit = true;
        else s.serveGuestHit = true;
        s.lastHitX = ev.x;
        s.lastHitY = ev.y;
        s.wallBouncesSinceHit = 0;
        s.peakSpeedSinceHit = 0;
      } else if (ev.e === 'wall') {
        s.wallBouncesSinceHit++;
      } else if (ev.e === 'goal') {
        // Update consecutive-scorer tracking BEFORE computing the tag —
        // hat-trick depends on the post-increment count.
        if (s.lastGoalScorer === ev.scorer) {
          s.consecutiveGoals++;
        } else {
          s.lastGoalScorer = ev.scorer;
          s.consecutiveGoals = 1;
        }
        const tag = computeGoalTag(s, ev.scorer, ev.y);
        if (tag !== undefined) ev.tag = tag;
      }
    }
  }
}

function computeGoalTag(s: GameState, scorer: 'host' | 'guest', goalY: number): GoalTag | undefined {
  const losing = scorer === 'host' ? 'guest' : 'host';
  const scorerHit = scorer === 'host' ? s.serveHostHit : s.serveGuestHit;
  const losingHit = losing === 'host' ? s.serveHostHit : s.serveGuestHit;
  const scorerScore = scorer === 'host' ? s.score.host : s.score.guest;
  const oppScore = scorer === 'host' ? s.score.guest : s.score.host;

  // ── Priority order — first match wins. Roughly: most specific +
  // rarest first, generic / common last. ────────────────────────────

  // Own goal: ONLY the losing side touched the puck this serve and put
  // it into their own goal. Conservative — a bot save that deflects
  // backward into the bot's own pocket is NOT an own goal.
  if (losingHit && !scorerHit && s.lastHitter === losing) return 'own';

  // Shutout: scorer at 5+ goals while opponent is still at 0. Fires on
  // any qualifying goal (5-0, 6-0, 7-0, …) — emphatic.
  if (scorerScore >= 5 && oppScore === 0) return 'shutout';

  // Hat trick: 3 consecutive goals by the same side. consecutiveGoals
  // was just incremented for this goal in the caller.
  if (s.consecutiveGoals === 3) return 'hat-trick';

  // Snipe: a goal post deflection happened within ~10 ticks before the
  // goal — the puck grazed iron on its way in.
  if (s.lastPostHitTick >= 0 && s.tick - s.lastPostHitTick <= 10) return 'snipe';

  // Comeback: even AFTER this goal, the scorer is still down 2+ goals.
  // Celebrates clutch play in a deficit (re-fires across multiple goals
  // while you're climbing back).
  if (oppScore - scorerScore >= 2) return 'comeback';

  // Ace: scorer hit, opponent never touched the puck this serve.
  if (scorerHit && !losingHit && s.lastHitter === scorer) return 'ace';

  // Remaining tags require a paddle hit to have occurred at all.
  if (s.lastHitter === null) return undefined;

  // Dunk: tap-in from very close range — last paddle touch was <160
  // world units from the goal.
  const goalX = scorer === 'host' ? RINK.width : 0;
  const goalCenterY = RINK.height / 2;
  const distToGoal = Math.hypot(s.lastHitX - goalX, s.lastHitY - goalCenterY);
  if (distToGoal < 160) return 'dunk';

  // Speedrun: goal scored within ~3 seconds of the serve starting
  // (3 sec × 60 Hz = 180 ticks). Frantic-opening type of goal.
  if (s.lastServeStartTick > 0 && s.tick - s.lastServeStartTick <= 180) return 'speedrun';

  // Long shot: puck traveled > 500 world units from last paddle touch.
  if (distToGoal > 500) return 'long';

  // Banger: peak puck speed since last hit was near the cap.
  if (s.peakSpeedSinceHit >= 16) return 'banger';

  // Toilet: slow-roller — the puck barely moved between the last hit
  // and the goal. Peak speed < 4 means it dribbled in.
  if (s.peakSpeedSinceHit < 4) return 'toilet';

  // Top shelf: goal placed in the outer 30% of the mouth (near a post).
  if (Math.abs(goalY - goalCenterY) > RINK.goalHalfHeight * 0.7) return 'top-shelf';

  // Bank shot: ≥2 wall bounces between last paddle touch and the goal.
  if (s.wallBouncesSinceHit >= 2) return 'bank';

  return undefined;
}

function movePaddle(p: Paddle, maxSpeed: number): void {
  p.prev.x = p.pos.x;
  p.prev.y = p.pos.y;
  const dx = p.target.x - p.pos.x;
  const dy = p.target.y - p.pos.y;
  const d = Math.hypot(dx, dy);
  if (d <= maxSpeed) {
    p.pos.x = p.target.x;
    p.pos.y = p.target.y;
  } else {
    p.pos.x += (dx / d) * maxSpeed;
    p.pos.y += (dy / d) * maxSpeed;
  }
}

function collide(
  puck: Puck,
  p: Paddle,
  whose: 'host' | 'guest',
  cfg: PhysicsConfig,
  events?: PhysicsEvent[],
): void {
  // Capsule paddle: clamp puck.y to the paddle's straight section
  // (along the y axis) before doing circle-circle. When paddleHalfHeight
  // == paddleRadius, this collapses to a regular circle test.
  const halfStraight = cfg.paddleHalfHeight - cfg.paddleRadius;
  const dyRaw = puck.pos.y - p.pos.y;
  const clampedDy = halfStraight > 0
    ? Math.max(-halfStraight, Math.min(halfStraight, dyRaw))
    : 0;
  // Nearest point on the paddle's spine to the puck.
  const spineX = p.pos.x;
  const spineY = p.pos.y + clampedDy;

  const dx = puck.pos.x - spineX;
  const dy = puck.pos.y - spineY;
  const minDist = cfg.puckRadius + cfg.paddleRadius;
  const d = Math.hypot(dx, dy);
  if (d >= minDist) return;

  // Degenerate overlap: puck and paddle are at (effectively) the same
  // point, so there's no well-defined push direction. Eject the puck in
  // a random direction with a small velocity so the next tick has a
  // sane geometry to resolve.
  if (d < 0.001) {
    const angle = Math.random() * Math.PI * 2;
    const ex = Math.cos(angle);
    const ey = Math.sin(angle);
    puck.pos.x = spineX + ex * minDist;
    puck.pos.y = spineY + ey * minDist;
    puck.vel.x = ex * 5;
    puck.vel.y = ey * 5;
    events?.push({
      e: 'hit', paddle: whose,
      x: puck.pos.x, y: puck.pos.y, speed: 5,
    });
    return;
  }

  // Push puck out (from the spine point, not paddle center).
  const nx = dx / d;
  const ny = dy / d;
  puck.pos.x = spineX + nx * minDist;
  puck.pos.y = spineY + ny * minDist;

  // Reflect velocity over normal, then add paddle's own velocity.
  const dot = puck.vel.x * nx + puck.vel.y * ny;
  if (dot < 0) {
    puck.vel.x -= 2 * dot * nx;
    puck.vel.y -= 2 * dot * ny;
  }
  const pvx = p.pos.x - p.prev.x;
  const pvy = p.pos.y - p.prev.y;
  puck.vel.x += pvx * 0.9;
  puck.vel.y += pvy * 0.9;

  // Give a small minimum kick so it doesn't stick.
  const sp = Math.hypot(puck.vel.x, puck.vel.y);
  if (sp < 3) {
    puck.vel.x = nx * 3;
    puck.vel.y = ny * 3;
  }

  events?.push({
    e: 'hit',
    paddle: whose,
    x: puck.pos.x,
    y: puck.pos.y,
    speed: Math.hypot(puck.vel.x, puck.vel.y),
  });
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Point-circle collision with a goal post. Posts are infinitely-small
 * points at the rim/mouth junction. A puck grazing one gets pushed out
 * along the post→puck normal and bounces if it was moving INTO the post.
 * If it was already moving away (e.g., post-collision push-out from the
 * pocket back side), we still resolve interpenetration but don't reflect.
 */
function collidePost(
  s: GameState,
  postX: number,
  postY: number,
  sp: number,
  events?: PhysicsEvent[],
): void {
  const dx = s.puck.pos.x - postX;
  const dy = s.puck.pos.y - postY;
  const d = Math.hypot(dx, dy);
  if (d >= s.config.puckRadius) return;

  let nx: number, ny: number;
  if (d < 0.001) {
    // Degenerate overlap: push opposite to the puck's velocity so it
    // bounces back the way it came (head-on collision case). If the
    // puck is also stationary, default to pushing toward the rink
    // interior so it leaves the post region.
    const vs = Math.hypot(s.puck.vel.x, s.puck.vel.y);
    if (vs > 0.001) {
      nx = -s.puck.vel.x / vs;
      ny = -s.puck.vel.y / vs;
    } else {
      nx = postX < RINK.width / 2 ? 1 : -1;
      ny = 0;
    }
  } else {
    nx = dx / d;
    ny = dy / d;
  }
  s.puck.pos.x = postX + nx * s.config.puckRadius;
  s.puck.pos.y = postY + ny * s.config.puckRadius;

  const dot = s.puck.vel.x * nx + s.puck.vel.y * ny;
  if (dot < 0) {
    s.puck.vel.x -= 2 * dot * nx;
    s.puck.vel.y -= 2 * dot * ny;
    events?.push({ e: 'wall', x: s.puck.pos.x, y: s.puck.pos.y, horizontal: false, speed: sp });
    // Record post graze for the 'snipe' goal-tag detector.
    s.lastPostHitTick = s.tick;
  }
}
